import { HTMLAttributes, FormEvent, useState, useRef } from 'react'
import { PaperAirplaneIcon } from '@heroicons/react/24/solid'
import { Spinner } from './Loading'
import JumpToLatest from './JumpToLatest'
import { useStickToBottom } from '../../hooks/useStickToBottom'

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp?: Date
}

export interface ChatProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  messages: Message[]
  onSendMessage: (message: string) => void
  isLoading?: boolean
  placeholder?: string
}

const Chat = ({ 
  messages, 
  onSendMessage, 
  isLoading = false,
  placeholder = 'Type your message...',
  className = '',
  ...props 
}: ChatProps) => {
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { containerRef, contentRef, showJumpToLatest, jumpToLatest } = useStickToBottom()

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (input.trim() && !isLoading) {
      onSendMessage(input.trim())
      setInput('')
      inputRef.current?.focus()
      // Sending is an explicit request to see what comes back.
      jumpToLatest()
    }
  }
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }
  
  return (
    <div className={`flex flex-col h-full ${className}`} {...props}>
      {/* Messages Container */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-6">
          <div ref={contentRef} className="max-w-3xl mx-auto space-y-4">
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Spinner size="sm" />
                <span className="text-sm">Thinking...</span>
              </div>
            )}
          </div>
        </div>
        <JumpToLatest visible={showJumpToLatest} onClick={jumpToLatest} />
      </div>
      
      {/* Input Area */}
      <div className="border-t border-border px-4 py-4">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="relative flex items-center">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isLoading}
              rows={1}
              className="
                w-full px-4 py-3 pr-12
                bg-background
                border-2 border-input
                rounded-lg text-sm
                placeholder:text-muted-foreground
                focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring
                disabled:opacity-50 disabled:cursor-not-allowed
                resize-none
              "
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="
                absolute right-2 top-1/2 -translate-y-1/2
                p-2 rounded-md
                bg-primary text-primary-foreground
                hover:bg-primary/90
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Press Enter to send, Shift+Enter for new line
          </p>
        </form>
      </div>
    </div>
  )
}

interface ChatMessageProps {
  message: Message
}

const ChatMessage = ({ message }: ChatMessageProps) => {
  const isUser = message.role === 'user'
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`
          max-w-[80%] px-4 py-3 rounded-lg
          ${isUser
            ? 'bg-accent text-foreground ml-auto'
            : 'bg-card text-foreground border border-border'
          }
        `}
      >
        <div className="text-sm whitespace-pre-wrap break-words">
          {message.content}
        </div>
        {message.timestamp && (
          <div className="mt-1 text-xs text-muted-foreground">
            {message.timestamp.toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  )
}

export default Chat

